import {WebGLBrandedDivider} from '@github-ui/brand-webgl'
import {ActionMenu, Button, Hero} from '@primer/react-brand'
import {getOS, type OS} from '@github-ui/get-os'
import {useEffect, useMemo, useState} from 'react'
import {clsx} from 'clsx'

import Styles from './CtaBanner.module.css'

export interface CtaBannerProps {
  label: string
  heading: string
  body: string
  cta1: {
    items: Array<{platform: OS; arch?: 'x86' | 'arm'; label: string; href: string}>
  }
  cta2: {
    label: string
    href: string
  }
}

export const CtaBanner = ({label, heading, body, cta1, cta2}: CtaBannerProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false)

  const resolvedOS: OS = useMemo(() => {
    if (!isMounted) return 'Windows'

    const os = getOS()
    return os.os === 'macOS' ? 'macOS' : os.os === 'Windows' ? 'Windows' : 'Linux'
  }, [isMounted])

  const resolvedInstaller = useMemo(() => cta1.items.find(item => item.platform === resolvedOS), [cta1, resolvedOS])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)
  }, [])

  return (
    <div className={clsx(Styles['CtaBanner'], !isMounted && Styles['CtaBanner--hidden'])}>
      <Hero
        className={Styles['CtaBanner__hero']}
        variant="gridline"
        align="center"
        enableAnimation
        aria-label={heading}
        aria-labelledby={undefined}
        trailingComponent={() => (
          <div className={Styles['CtaBanner__ctas']}>
            <ActionMenu mode="split-button">
              <ActionMenu.Button variant="accent" as="a" href={resolvedInstaller?.href}>
                {resolvedInstaller?.label}
              </ActionMenu.Button>
              <ActionMenu.Overlay aria-label={resolvedInstaller?.label || ''}>
                {cta1.items
                  .slice()
                  .sort((a, b) => {
                    const platformDiff = (a.platform === resolvedOS ? 0 : 1) - (b.platform === resolvedOS ? 0 : 1)
                    if (platformDiff !== 0) return platformDiff
                    const archOrder = (platform: OS, arch?: 'x86' | 'arm') => {
                      const preferred = platform === 'macOS' ? 'arm' : 'x86'
                      return arch === preferred ? 0 : arch ? 1 : 2
                    }
                    return archOrder(a.platform, a.arch) - archOrder(b.platform, b.arch)
                  })
                  .map(item => (
                    <ActionMenu.Item key={item.label} as="a" href={item.href}>
                      {item.label}
                    </ActionMenu.Item>
                  ))}
              </ActionMenu.Overlay>
            </ActionMenu>

            <Button as="a" href={cta2.href} variant="subtle" hasArrow={false}>
              {cta2.label}
            </Button>
          </div>
        )}
      >
        <Hero.Label>{label}</Hero.Label>
        <Hero.Heading {...({as: 'h2'} as Record<string, unknown>)}>{heading}</Hero.Heading>

        <Hero.Description>{body}</Hero.Description>
      </Hero>

      <WebGLBrandedDivider
        className={Styles['CtaBanner__brandedDivider']}
        params={{
          character: 'invertocat',
          layout: 1,
        }}
      />
    </div>
  )
}
